import { useEffect } from "react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Heading, Text } from "@/components/ui/typography"
import { semanticTokens } from "@supabank/config/theme"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

export type FAQItem = { id: string; q: string; a: string }
export type FAQAccordionProps = { id?: string; items: FAQItem[] }

export function FAQAccordion({ id, items }: FAQAccordionProps) {
  const { t } = useTranslation("marketing")
  
  // Add structured data (JSON-LD) for SEO and LLM optimization
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": items.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    }

    // Remove existing FAQ structured data if any
    const existingScript = document.querySelector('script[data-faq-structured-data]')
    if (existingScript) {
      existingScript.remove()
    }

    // Add new structured data
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-faq-structured-data', 'true')
    script.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script)

    return () => {
      const scriptToRemove = document.querySelector('script[data-faq-structured-data]')
      if (scriptToRemove) {
        scriptToRemove.remove()
      }
    }
  }, [items])

  return (
    <section 
      id={id} 
      aria-labelledby="faq-title" 
      className={cn(
        "relative py-16 sm:py-24 overflow-hidden",
        "outline-none", // Remove any focus outline (section is not interactive)
        semanticTokens.bg.surface
      )}
      tabIndex={-1} // Prevent focus outline (section is not interactive)
    >
      {/* Color Lab: Subtle Lavender gradient */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 80% 20%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%),
            radial-gradient(ellipse at 20% 80%, hsla(217, 91%, 60%, 0.06) 0%, transparent 45%)
          `
        }}
      />
      <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6">
        <div className="space-y-2 sm:space-y-3">
          <Text variant="caption" color="muted" className="uppercase tracking-widest">
            {t("faq.badge")}
          </Text>
          <Heading level={2} id="faq-title">
            {t("faq.headline")}
          </Heading>
          <Text variant="sm" color="muted">
            {t("faq.description")}
          </Text>
        </div>
        <Accordion type="single" collapsible className="mt-6 sm:mt-8" role="presentation">
          {items.map((item) => (
            <AccordionItem 
              key={item.id} 
              value={item.id} 
              className="border-b" 
              data-id={item.id}
            >
              <AccordionTrigger 
                className={cn(
                  "text-left font-body text-body-sm sm:text-body-md font-medium",
                  "min-h-[44px]", // Touch target ≥44px
                  "py-3 sm:py-4",
                  semanticTokens.fg.default
                )}
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent className={cn(
                "font-body text-body-sm sm:text-body-md leading-relaxed",
                "pb-4 sm:pb-5",
                semanticTokens.fg.muted
              )}>
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  )
}
